import { useEffect } from 'react';
import { Link, Outlet, useParams } from 'react-router-dom';
import { useTenantContext } from '@/auth/TenantContext';
import { AppShell } from '@/components/AppShell';
import { buttonClasses } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';

/**
 * A single, non-leaking "workspace not available" state (spec 0013, ADR-0003).
 * Shown for ANY `:workspaceSlug` that is not among the caller's memberships --
 * whether it does not exist or exists but the caller is not a member. The two
 * cases are deliberately indistinguishable: resolution is only ever against the
 * caller's own memberships, with NO cross-tenant existence probe, so the SPA
 * never discloses whether another workspace exists.
 */
function WorkspaceNotAvailable() {
  return (
    <div
      role="alert"
      data-testid="workspace-not-available"
      className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center"
    >
      <h1 className="text-2xl font-semibold text-gray-900">Workspace not available</h1>
      <p className="max-w-md text-sm text-gray-500">
        This workspace does not exist or you do not have access to it.
      </p>
      <Link to="/" className={buttonClasses()}>
        Go to your workspace
      </Link>
    </div>
  );
}

/**
 * Parent layout for the entire authenticated SPA under `/:workspaceSlug`
 * (spec 0013, ADR-0003). Resolves the slug against the caller's memberships:
 * a match sets it as the active tenant (so `getActiveTenantId()`, the
 * `X-Tenant-Id` header, and every per-tenant query key follow the URL -- AC-4b);
 * anything else renders the single non-leaking not-available state (AC-11).
 * The active tenant is derived from the URL, not the other way round.
 */
export function WorkspaceLayout() {
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
  const { tenants, activeTenantId, setActiveTenant } = useTenantContext();

  const membership = tenants.find((t) => t.slug === workspaceSlug);

  // The URL drives the active workspace: when the slug names a membership that
  // is not the active one, switch to it (AC-4, AC-4b). Never touch the active
  // tenant for an unresolved slug.
  useEffect(() => {
    if (membership && membership.id !== activeTenantId) {
      setActiveTenant(membership.id);
    }
  }, [membership, activeTenantId, setActiveTenant]);

  if (!membership) {
    return <WorkspaceNotAvailable />;
  }

  // Hold render until the active tenant matches the URL, so child pages never
  // fetch one workspace's data under another's tenant header.
  if (activeTenantId !== membership.id) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <Spinner label="Loading workspace" />
      </div>
    );
  }

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}
